/**
 * Layer 7 — orders & plans.
 *
 *  - Active orders of own units: curved operation arrows along their planned route (move =
 *    light, attack = bold, retreat = dashed), in the faction colour.
 *  - Queued (H-hour) orders: fainter dashed arrows with a clock tag "H 06:00".
 *  - Couriers in transit: dashed line from the rider to the addressee, an animated courier
 *    glyph at its interpolated position, and the pending order as a dashed arrow.
 *  - Telephone / radio orders on the wire (`view.dispatches`): thin dashed arrows.
 *  - Intercepted enemy orders: red arrows with a stencilled "INTERCEPTED" label.
 *  - Hover preview while units are selected (styled by the active tool), the hovered hex, and
 *    the telephone command radius around selected HQs.
 *  - Omniscient replay view (`faction` = SPECTATOR): every faction's orders, couriers and
 *    dispatches, each in its own colour.
 * Static parts rebuild when the view / selection / hover / tool change; couriers per frame.
 */
import {
  COMMAND_RADIUS,
  EDGE_CORNERS,
  type FactionId,
  type HexGrid,
  type HexId,
  hourOfDay,
  NO_HEX,
  type PlayerView,
  SPECTATOR,
  type Unit,
} from '@friction/engine';
import { Container, Text, TextStyle, type Texture } from 'pixi.js';
import type { MapTool } from '../../game/store';
import { getLocale, tr } from '../../i18n';
import type { MarkerGlyphs } from '../atlas/glyphs';
import { MONO, STENCIL } from '../fonts';
import { addArrow, bowedRoute } from '../geom/arrows';
import { chaikin, chainSegments } from '../geom/paths';
import { LineBatch } from '../gfx/lines';
import { QuadBatch, Scale } from '../gfx/quads';
import { cssColor, darken, lighten, PAL } from '../palette';
import { unitPos } from './units';

export interface OrdersInput {
  view: PlayerView;
  prev: PlayerView | null;
  selection: readonly number[];
  hoverHex: HexId;
  tool: MapTool;
  hostileAtHover: boolean;
  colors: ReadonlyMap<FactionId, number>;
  /** Draw the would-be order from the selection to the hovered hex (never in a replay). */
  preview: boolean;
}

interface Tag {
  text: Text;
  x: number;
  y: number;
  /** Offset in css px (kept constant on screen). */
  ox: number;
  oy: number;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
/** Clock tag of an order with an H-hour: "H 06:00" / "X 06:00" (German: X-Zeit). */
const hTag = (tick: number): string =>
  tr(getLocale())('time.hhourTag', { hour: `${pad2(hourOfDay(tick))}:00` });

export class OrdersLayer {
  readonly container = new Container({ label: 'orders' });
  private readonly arrows = new LineBatch('order-arrows', 8192);
  private readonly overlay = new LineBatch('order-overlay', 1024);
  private readonly courierLines = new LineBatch('courier-lines', 512);
  private readonly courierGlyphs: QuadBatch;
  private readonly tagLayer = new Container({ label: 'order-tags' });
  private readonly tagPool: Text[] = [];
  private readonly interceptPool: Text[] = [];
  private tags: Tag[] = [];
  private readonly grid: HexGrid;
  private readonly glyphs: MarkerGlyphs;
  private view: PlayerView | null = null;
  private prev: PlayerView | null = null;

  constructor(grid: HexGrid, glyphs: MarkerGlyphs, atlasTexture: Texture) {
    this.grid = grid;
    this.glyphs = glyphs;
    this.courierGlyphs = new QuadBatch('couriers', atlasTexture, 128);
    this.container.addChild(
      this.overlay.mesh,
      this.arrows.mesh,
      this.courierLines.mesh,
      this.courierGlyphs.mesh,
      this.tagLayer,
    );
  }

  private centre(h: HexId): [number, number] {
    return [this.grid.cx(h), this.grid.cy(h)];
  }

  rebuild(inp: OrdersInput): void {
    const view = inp.view;
    const grid = this.grid;
    this.view = view;
    this.prev = inp.prev;
    const a = this.arrows;
    a.clear();
    const me = view.faction;
    const all = me === SPECTATOR;
    // Orders are drawn in their faction's colour (only ever our own, except when omniscient).
    let fc: number = PAL.ink;
    let casing: number = PAL.ink;
    const paint = (f: FactionId) => {
      fc = inp.colors.get(f) ?? PAL.ink;
      casing = darken(fc, 0.6);
    };
    let tagN = 0;
    let icN = 0;
    this.tags = [];
    // clock tags sit right of the counter; several at one place stack downwards
    const perPlace = new Map<string, number>();
    const tag = (s: string, x: number, y: number) => {
      const key = `${Math.round(x)},${Math.round(y)}`;
      const k = perPlace.get(key) ?? 0;
      perPlace.set(key, k + 1);
      const t = this.tagPool[tagN] ?? this.newTag();
      tagN++;
      if (t.text !== s) t.text = s;
      this.tags.push({ text: t, x, y, ox: 31, oy: -9 + k * 13 });
    };

    const routeOf = (u: Unit, target: HexId): number[] => {
      const [x, y] = unitPos(grid, u);
      const pts = [x, y];
      const path = u.path.length && u.path[u.path.length - 1] === target ? u.path : [];
      for (const h of path) pts.push(grid.cx(h), grid.cy(h));
      if (!path.length) return bowedRoute(x, y, grid.cx(target), grid.cy(target));
      return chaikin(pts, 3).pts;
    };

    for (const u of view.units) {
      if (u.faction !== me && !all) continue;
      paint(u.faction);
      const o = u.order;
      if (
        o &&
        o.target >= 0 &&
        (o.kind === 'move' || o.kind === 'attack' || o.kind === 'retreat') &&
        o.target !== u.hex
      ) {
        const pts = routeOf(u, o.target);
        if (o.kind === 'attack')
          addArrow(a, pts, {
            color: darken(fc, 0.12),
            alpha: 0.8,
            width: 5.6,
            casing,
            casingAlpha: 0.5,
            minPx: 3.6,
            maxPx: 12,
          });
        else if (o.kind === 'retreat')
          addArrow(a, pts, {
            color: fc,
            alpha: 0.6,
            width: 3.8,
            casing,
            casingAlpha: 0.35,
            dashed: true,
            minPx: 2.6,
            maxPx: 8,
          });
        else
          addArrow(a, pts, {
            color: fc,
            alpha: 0.55,
            width: 3.8,
            casing,
            casingAlpha: 0.35,
            minPx: 2.6,
            maxPx: 8,
          });
      }
      const q = u.queuedOrder;
      if (q && q.target >= 0 && q.kind !== 'posture' && q.kind !== 'hold') {
        const [x, y] = unitPos(grid, u);
        const [tx, ty] = this.centre(q.target);
        addArrow(a, bowedRoute(x, y, tx, ty, 0.12), {
          color: lighten(fc, 0.15),
          alpha: 0.5,
          width: q.kind === 'attack' ? 4.6 : 3.6,
          casing,
          casingAlpha: 0.3,
          dashed: true,
          minPx: 2.4,
          maxPx: 9,
        });
        if (q.executeAt >= 0) tag(hTag(q.executeAt), x, y);
      } else if (o && u.status === 'awaiting' && o.executeAt >= 0) {
        const [x, y] = unitPos(grid, u);
        tag(hTag(o.executeAt), x, y);
      }
    }

    // orders still riding with a courier: dashed, from the addressee to the objective
    for (const c of view.couriers) {
      const u = view.units.find((x) => x.id === c.targetUnit);
      const o = c.order;
      if (!u || o.target < 0 || o.kind === 'posture' || o.kind === 'hold') continue;
      paint(c.faction);
      const [x, y] = unitPos(grid, u);
      const [tx, ty] = this.centre(o.target);
      addArrow(a, bowedRoute(x, y, tx, ty, 0.14), {
        color: lighten(fc, 0.3),
        alpha: 0.42,
        width: 3.4,
        casing,
        casingAlpha: 0.22,
        dashed: true,
        minPx: 2.2,
        maxPx: 8,
      });
      if (o.executeAt >= 0) tag(hTag(o.executeAt), x, y);
    }

    // telephone / radio orders on the wire: thin dashed arrows, no courier glyph
    for (const d of view.dispatches ?? []) {
      const o = d.order;
      const u = view.units.find((x) => x.id === o.unitId);
      if (!u || o.target < 0 || o.kind === 'posture' || o.kind === 'hold') continue;
      paint(o.faction);
      const [x, y] = unitPos(grid, u);
      const [tx, ty] = this.centre(o.target);
      addArrow(a, bowedRoute(x, y, tx, ty, 0.12), {
        color: fc,
        alpha: 0.42,
        width: 2.8,
        casing,
        casingAlpha: 0.2,
        dashed: true,
        minPx: 2,
        maxPx: 6,
      });
      if (o.executeAt >= 0) tag(hTag(o.executeAt), x, y);
    }

    // intercepted enemy plans
    for (const ic of view.intercepts) {
      if (ic.order.target < 0 || ic.fromHex < 0) continue;
      const [x0, y0] = this.centre(ic.fromHex);
      const [x1, y1] = this.centre(ic.order.target);
      addArrow(a, bowedRoute(x0, y0, x1, y1, -0.15), {
        color: PAL.danger,
        alpha: 0.78,
        width: 5,
        casing: 0x3a0e0a,
        casingAlpha: 0.5,
        minPx: 3.2,
        maxPx: 11,
      });
      const t = this.interceptPool[icN] ?? this.newIntercept();
      icN++;
      const tl = tr(getLocale());
      const label = ic.order.opName
        ? tl('map.interceptedOp', { op: ic.order.opName.toUpperCase() })
        : tl('map.intercepted');
      if (t.text !== label) t.text = label;
      this.tags.push({ text: t, x: (x0 + x1) / 2, y: (y0 + y1) / 2, ox: 0, oy: -16 });
    }

    // hover preview for the current selection
    const hover = inp.hoverHex;
    if (inp.preview && inp.selection.length && hover !== NO_HEX) {
      const kind = inp.tool === 'select' ? (inp.hostileAtHover ? 'attack' : 'move') : inp.tool;
      const sel = view.units.filter((u) => inp.selection.includes(u.id));
      const [hx, hy] = this.centre(hover);
      const col = kind === 'attack' ? PAL.danger : PAL.brassLight;
      const style = {
        color: col,
        alpha: 0.85,
        width: kind === 'attack' ? 5 : 3.8,
        casing: PAL.ink,
        casingAlpha: 0.45,
        dashed: kind === 'retreat',
        minPx: 2.6,
        maxPx: 10,
      };
      if (sel.length > 12) {
        let sx = 0;
        let sy = 0;
        for (const u of sel) {
          const [x, y] = unitPos(grid, u);
          sx += x;
          sy += y;
        }
        addArrow(a, bowedRoute(sx / sel.length, sy / sel.length, hx, hy, 0.1), {
          ...style,
          width: style.width * 1.4,
        });
      } else {
        for (const u of sel) {
          const [x, y] = unitPos(grid, u);
          if (Math.hypot(hx - x, hy - y) > 20) addArrow(a, bowedRoute(x, y, hx, hy, 0.1), style);
        }
      }
    }
    a.commit();

    for (let i = tagN; i < this.tagPool.length; i++) this.tagPool[i].visible = false;
    for (let i = icN; i < this.interceptPool.length; i++) this.interceptPool[i].visible = false;
    for (const t of this.tags) t.text.visible = true;

    // hovered hex + command radius of selected HQs
    const ov = this.overlay;
    ov.clear();
    if (hover !== NO_HEX) {
      const ring: number[] = [];
      for (let i = 0; i < 6; i++) {
        const p = grid.corner(hover, i);
        ring.push(p.x, p.y);
      }
      const col =
        inp.preview && inp.selection.length
          ? inp.tool === 'attack' || inp.hostileAtHover
            ? PAL.danger
            : PAL.brassLight
          : PAL.brassLight;
      ov.add(ring, { width: 3.6, color: PAL.ink, alpha: 0.45, minPx: 3, maxPx: 4.4 }, { closed: true });
      ov.add(ring, { width: 1.8, color: col, alpha: 0.95, minPx: 1.4, maxPx: 2.4 }, { closed: true });
    }
    const radius = COMMAND_RADIUS + (view.me.research.unlocked.includes('field_telephone') ? 2 : 0);
    for (const u of view.units) {
      if (u.type !== 'hq' || u.faction !== me || !inp.selection.includes(u.id)) continue;
      const inside = new Set(grid.range(u.hex, radius));
      const segs: number[] = [];
      for (const h of inside) {
        for (let d = 0; d < 6; d++) {
          const n = grid.neighbour(h, d);
          if (n >= 0 && inside.has(n)) continue;
          const [ca, cb] = EDGE_CORNERS[d];
          const p = grid.corner(h, ca);
          const q = grid.corner(h, cb);
          segs.push(p.x, p.y, q.x, q.y);
        }
      }
      for (const c of chainSegments(segs)) {
        ov.add(
          c.pts,
          { width: 3.4, color: PAL.ink, alpha: 0.4, minPx: 3, maxPx: 4.2, dash: 9, gap: 3, dashPx: true },
          { closed: c.closed },
        );
        ov.add(
          c.pts,
          {
            width: 1.6,
            color: PAL.brassLight,
            alpha: 0.95,
            minPx: 1.5,
            maxPx: 2.2,
            dash: 7,
            gap: 5,
            dashPx: true,
            dist0: -1,
          },
          { closed: c.closed },
        );
      }
    }
    ov.commit();
  }

  private newTag(): Text {
    const t = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: MONO,
        fontSize: 10.5,
        fontWeight: '600',
        fill: cssColor(PAL.ink),
        stroke: { color: 0xf3ecdb, width: 3.4, join: 'round' },
        letterSpacing: 0.4,
      }),
      resolution: 3,
      anchor: { x: 0, y: 0.5 },
    });
    this.tagPool.push(t);
    this.tagLayer.addChild(t);
    return t;
  }

  private newIntercept(): Text {
    const t = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: STENCIL,
        fontSize: 13,
        fill: cssColor(0x9e2219),
        stroke: { color: 0xf3ecdb, width: 3.4, join: 'round', alpha: 0.9 },
        letterSpacing: 2.5,
      }),
      resolution: 3,
      anchor: 0.5,
    });
    t.rotation = -0.06;
    this.interceptPool.push(t);
    this.tagLayer.addChild(t);
    return t;
  }

  /** Per frame: zoom uniforms, tag scale, couriers at their interpolated positions. */
  frame(
    t: number,
    zoom: number,
    resolution: number,
    markerUnit: number,
    positionOf: (id: number) => [number, number] | null,
  ): void {
    this.arrows.setZoom(zoom, resolution);
    this.overlay.setZoom(zoom, resolution);
    this.courierLines.setZoom(zoom, resolution);
    this.courierGlyphs.setUnits(markerUnit, markerUnit);
    const s = Math.min(1.25, Math.max(0.8, 0.75 + zoom * 0.3)) / zoom;
    for (const tg of this.tags) {
      tg.text.scale.set(s);
      tg.text.position.set(tg.x + (tg.ox * s * zoom) / zoom, tg.y + tg.oy / zoom);
    }
    const v = this.view;
    const cl = this.courierLines;
    const cg = this.courierGlyphs;
    cl.clear();
    cg.clear();
    if (v) {
      const prevById = new Map((this.prev?.couriers ?? []).map((c) => [c.id, c]));
      for (const c of v.couriers) {
        const [x1, y1] = unitPos(this.grid, c);
        const p = prevById.get(c.id);
        const [x0, y0] = p ? unitPos(this.grid, p) : [x1, y1];
        const x = x0 + (x1 - x0) * t;
        const y = y0 + (y1 - y0) * t;
        const target = positionOf(c.targetUnit);
        if (target)
          cl.add([x, y, target[0], target[1]], {
            width: 1.3,
            color: PAL.ink,
            alpha: 0.6,
            minPx: 1.1,
            maxPx: 1.8,
            dash: 5,
            gap: 4,
            dashPx: true,
          });
        const g = c.mode === 'motorcycle' ? this.glyphs.motorcycle : this.glyphs.rider;
        cg.push(x, y, 0, 0, g.w, g.h, g.frame, 0xffffff, 1, Scale.Marker);
      }
    }
    cl.commit();
    cg.commit();
  }

  destroy(): void {
    this.arrows.destroy();
    this.overlay.destroy();
    this.courierLines.destroy();
    this.courierGlyphs.destroy();
    this.container.destroy({ children: true });
  }
}
